import { styled, SxProp, View } from 'dripsy';
import moment, { Moment } from 'moment-timezone';
import React from 'react';
import { Pressable } from 'react-native';

import { keyGenerator } from '@/utils/keyGenerator';
import { useStore } from 'store/store';
import { Icon } from '../icons/Icon';
import { TextBody, TextCaption } from '../typography';
import { debounce } from 'lodash';
import { UniInput } from '../inputs';
import { UseInputRegularVariant } from '../inputs/useInput.hook';

const PressableView = styled(Pressable)();
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const Calendar: React.FC<{
  side: 'LEFT' | 'RIGHT' | 'SINGLE';
  mode: 'DATE_RANGE' | 'DATE';
  selectedDateRange: { from: Moment | null; to: Moment | null };
  selectedDate: Moment | null;
  calendar: Array<Array<Moment | null>>;
  options?: {
    minDate?: Moment | null;
    maxDate?: Moment | null;
    yearInputFlag?: boolean;
  };
  onSelectDate: (date: Moment | null) => void;
  onPreviousMonth: () => void;
  onNextMonth: () => void;
  onPreviousYear: () => void;
  onNextYear: () => void;
  onYear?: (year: number) => void;
}> = ({
  side,
  mode,
  selectedDateRange,
  selectedDate,
  calendar,
  options,
  onSelectDate,
  onNextMonth,
  onPreviousMonth,
  onPreviousYear,
  onNextYear,
  onYear
}) => {
  const { minDate, maxDate, yearInputFlag = false } = options || {};
  const store = useStore();
  const locale = store.locale;

  return (
    <View sx={styles.calendar}>
      {/* MMMM YY */}
      <View sx={styles.calendarHeader}>
        <View sx={{ flex: 1.5, flexDirection: 'row', alignItems: 'center' }}>
          <NavButton type="PREVIOUS" onPress={onPreviousMonth} />
          <TextBody
            sx={{
              flex: 1,
              textAlign: 'center'
            }}
            semiBold>
            {calendar[1]?.[0]?.locale(locale).format('MMMM')}
          </TextBody>
          <NavButton type="NEXT" onPress={onNextMonth} />
        </View>
        <View sx={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}>
          <NavButton type="PREVIOUS" onPress={onPreviousYear} />
          {yearInputFlag && !!onYear ? (
            <View
              sx={{
                flex: 1,
                textAlign: 'center',
                mx: 30
              }}>
              <UniInput
                regular={UseInputRegularVariant.number}
                digits={4}
                size="medium"
                sx={{ border: 'none' }}
                inputSx={{ width: '100%', textAlign: 'center' }}
                value={calendar[1]?.[0]?.format('YYYY')}
                onValueChange={debounce((value) => {
                  if (value && calendar[1]?.[0]?.format('YYYY') && calendar[1]?.[0]?.format('YYYY') != value) onYear(Number(value));
                }, 1000)}
              />
            </View>
          ) : (
            <TextBody
              sx={{
                flex: 1,
                textAlign: 'center'
              }}
              semiBold>
              {calendar[1]?.[0]?.format('YYYY')}
            </TextBody>
          )}
          <NavButton type="NEXT" onPress={onNextYear} />
        </View>
      </View>
      {/* Header */}
      <View sx={styles.calendarRow}>
        {DAYS.map((day, i) => (
          <DateCol key={keyGenerator(`date-picker-${side}`)} onPress={() => {}} isDate={false}>
            {/* <TextCaption secondary>{day}</TextCaption> */}
            <TextCaption secondary>{moment().locale(locale).day(i).format('ddd')}</TextCaption>
          </DateCol>
        ))}
      </View>
      {/* Days */}
      <View sx={{ gap: '$1' }}>
        {calendar.map((week, i) => (
          <View key={keyGenerator('')} sx={styles.calendarRow}>
            {week.map((day, j) => {
              const isDateRange = mode === 'DATE_RANGE';
              const isSelected = isDateRange
                ? day?.isSame(selectedDateRange.from, 'day') || day?.isSame(selectedDateRange.to, 'day')
                : day?.isSame(selectedDate, 'day');

              return (
                <DateCol
                  key={keyGenerator(`date-picker-${side}-${i}-${j}`)}
                  onPress={() => onSelectDate(day)}
                  isSelected={isSelected}
                  isHighlighted={isDateRange ? day?.isBetween(selectedDateRange.from, selectedDateRange.to, 'day', '()') : false}
                  selectedStart={isDateRange ? day?.isSame(selectedDateRange.from, 'day') : false}
                  selectedEnd={isDateRange ? day?.isSame(selectedDateRange.to, 'day') : false}
                  isDate={!!day}
                  selectedDateRange={selectedDateRange}
                  mode={mode}
                  date={day}
                  options={options}></DateCol>
              );
            })}
          </View>
        ))}
      </View>
    </View>
  );
};

const NavButton: React.FC<{ type: 'PREVIOUS' | 'NEXT'; sx?: SxProp; onPress: () => void }> = ({ type, sx, onPress }) => {
  const baseStyle: SxProp = { ...styles.navButton, position: 'absolute' };
  const style = type === 'PREVIOUS' ? { ...baseStyle, left: 0 } : { ...baseStyle, right: 0 };

  return (
    <PressableView onPress={onPress} sx={{ ...style, ...sx }}>
      <Icon name={type === 'PREVIOUS' ? 'arrowLeft' : 'arrowRight'} />
    </PressableView>
  );
};

const DateCol: React.FC<{
  mode?: 'DATE_RANGE' | 'DATE';
  isDate: boolean;
  isSelected?: boolean;
  isHighlighted?: boolean;
  selectedStart?: boolean;
  selectedEnd?: boolean;
  label?: string;
  onPress: () => void;
  selectedDateRange?: {
    from: Moment | null;
    to: Moment | null;
  };
  date?: Moment | null;
  options?: {
    minDate?: Moment | null;
    maxDate?: Moment | null;
  };
}> = ({
  mode = 'DATE_RANGE',
  isDate,
  isSelected,
  isHighlighted,
  label,
  children,
  selectedStart,
  selectedEnd,
  onPress,
  selectedDateRange,
  date,
  options
}) => {
  const { minDate, maxDate } = options || {};
  let baseStyle = styles.dateCol;
  if (isHighlighted && !isSelected) {
    baseStyle = { ...baseStyle, ...styles.dateColHighlighted };
  }
  let disabledPress = false;
  if (date && ((minDate && date.isBefore(minDate, 'day')) || (maxDate && date.isAfter(maxDate, 'day')))) {
    disabledPress = true;
  }
  return (
    <PressableView onPress={!disabledPress ? onPress : undefined} sx={{ flex: 1 }}>
      {({ hovered }) => {
        let style = baseStyle;
        if (isHighlighted) {
          style = { ...style, ...styles.dateColHighlighted };
        }

        const disabled = mode === 'DATE_RANGE' ? !!(date && selectedDateRange?.to && date.isAfter(selectedDateRange?.to)) : false;
        let textStyle = {
          color: isSelected || hovered ? '$datePickerSelectedText' : '$datePickerText'
        };
        if (isHighlighted) textStyle.color = '$datePickerHighlightedText';
        if (disabled || disabledPress) textStyle.color = '$textDisable';

        return (
          <View sx={style}>
            {isDate && (isSelected || hovered) && (
              <View
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: '50%',
                  width: '$10',
                  height: '$10',
                  backgroundColor: '$primary',
                  transform: [{ translateX: -20 }, { translateY: 0 }],
                  borderRadius: '50%',
                  zIndex: -1
                }}
              />
            )}
            {selectedStart && <View sx={styles.selectedStart} />}
            {selectedEnd && <View sx={styles.selectedEnd} />}
            {label && <TextBody>{label}</TextBody>}
            {children && children}
            {date && <TextCaption sx={textStyle}>{date ? date.format('D') : ''}</TextCaption>}
          </View>
        );
      }}
    </PressableView>
  );
};

enum StyleName {
  navButton = 'navButton',
  calendar = 'calendar',
  calendarHeader = 'calendarHeader',
  calendarRow = 'calendarRow',
  dateCol = 'dateCol',
  dateColSelected = 'dateColSelected',
  dateColHighlighted = 'dateColHighlighted',
  rangeSelect = 'rangeSelect',
  selectedStart = 'selectedStart',
  selectedEnd = 'selectedEnd'
}

const styles: Record<StyleName, SxProp> = {
  navButton: {
    height: '$10',
    width: '$10',
    justifyContent: 'center',
    alignItems: 'center'
  },
  calendar: {
    flex: 1
  },
  calendarHeader: {
    height: '$10',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  calendarRow: {
    flex: 1,
    flexDirection: 'row'
  },
  dateCol: {
    minWidth: '$10',
    width: '100%',
    height: '$10',
    justifyContent: 'center',
    alignItems: 'center'
  },
  dateColSelected: {
    position: 'absolute',
    top: 0,
    left: '50%',
    width: '$10',
    height: '$10',
    // transform: [{ translateX('-50%') }],
    backgroundColor: '$datePickerSelected',
    borderRadius: '50%',
    zIndex: -1
  },
  dateColHighlighted: {
    backgroundColor: '$datePickerHighlightedBackground'
  },
  rangeSelect: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center'
    // gap: '$5'
  },
  selectedStart: {
    position: 'absolute',
    top: 0,
    left: '50%',
    width: '50%',
    height: '100%',
    zIndex: -10,
    backgroundColor: '$datePickerHighlightedBackground'
  },
  selectedEnd: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '50%',
    height: '100%',
    zIndex: -10,
    backgroundColor: '$datePickerHighlightedBackground'
  }
};
